import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useTeamMembers, TeamMember } from '../../hooks/useTeamMembers';
import { useTeamActions } from '../../hooks/useTeamActions';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { usePermissions } from '../../lib/permissions';
import { supabase } from '../../lib/supabase';
import Button from '../../components/Button/Button';
import Dialog from '../../components/Dialog/Dialog';
import Dropdown from '../../components/Dropdown/Dropdown';
import EmptyView from '../shared/EmptyView';
import EmptyStateEnhanced from '../../components/EmptyStateEnhanced';
import InviteDialog from '../../components/InviteDialog/InviteDialog';
import EditTeamDialog from '../../components/EditTeamDialog/EditTeamDialog';
import Input from '../../components/Input/Input';
import PaymentManager from '../../components/PaymentManager/PaymentManager';
// @ts-ignore
import addIcon from '../../image-assets/icon.add.svg';
// @ts-ignore
import deleteIcon from '../../image-assets/icon.delete.svg';
// @ts-ignore
import profileIcon from '../../image-assets/icon.profile.svg';
import '../SharedStyles.css';
import './TeamView.css';
import { useNavigate, useLocation } from 'react-router-dom';

// Add this type for our test role
type TestRole = 'owner' | 'admin' | 'reviewer';

const TeamView: React.FC = () => {
  const { selectedTeam, setSelectedTeam, deleteTeam, isLoading: isWorkspaceLoading } = useWorkspace();
  const { user } = useAuth();
  const { profileData } = useProfile();
  const { 
    members, 
    invites, 
    isLoading: isTeamMembersLoading, 
    error,
    setMembers,
    setInvites,
    setError 
  } = useTeamMembers();
  const { 
    deleteTeamMember, 
    deleteTeamInvite, 
    inviteTeamMember
  } = useTeamActions();
  const { permissions, isOwner, isAdmin } = usePermissions(selectedTeam, user?.id, profileData?.id);
  const [searchQuery, setSearchQuery] = useState('');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isEditTeamDialogOpen, setIsEditTeamDialogOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'member' | 'invite', id: string } | null>(null);
  const [isInviting, setIsInviting] = useState(false);
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  // State to control content animation
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [testRole, setTestRole] = useState<TestRole>('owner');
  const navigate = useNavigate();
  const location = useLocation();

  // Botan onboarding: If ?checkout=success, redirect to overview
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'success') {
      navigate('/app/overview', { replace: true });
    }
  }, [location.search, navigate]);

  // Combined loading state
  const isLoading = isWorkspaceLoading || isTeamMembersLoading;

  // Check if user can manage roles (owner only)
  const canManageRoles = permissions.canManageRoles;

  // Check if current user is the owner
  const isCurrentUserOwner = profileData?.id === selectedTeam?.owner_profile_id;

  // Development mode role toggle
  const handleTestRoleChange = (role: TestRole) => {
    setTestRole(role);
    // Update the team's owner_profile_id and admins array based on the test role
    if (selectedTeam) {
      const updatedTeam = { ...selectedTeam };
      if (role === 'owner') {
        updatedTeam.owner_profile_id = profileData?.id || '';
        updatedTeam.admins = [];
      } else if (role === 'admin') {
        updatedTeam.owner_profile_id = 'test-owner-id';
        updatedTeam.admins = [profileData?.id || ''];
      } else {
        updatedTeam.owner_profile_id = 'test-owner-id';
        updatedTeam.admins = ['test-admin-id'];
      }
      setSelectedTeam(updatedTeam);
    }
  };

  // Listen for search query updates from the toolbar
  useEffect(() => {
    const handleSearchUpdate = (event: CustomEvent<{ searchQuery: string }>) => {
      setSearchQuery(event.detail.searchQuery);
    };

    const handleOpenInviteDialog = () => {
      if (permissions.canAddUsers) {
        setIsInviteDialogOpen(true);
      }
    };

    const handleOpenEditTeamDialog = () => {
      if (permissions.canEditTeamInfo) {
        setIsEditTeamDialogOpen(true);
      }
    };

    window.addEventListener('update-search-query', handleSearchUpdate as EventListener);
    window.addEventListener('open-invite-dialog', handleOpenInviteDialog);
    window.addEventListener('open-edit-team-dialog', handleOpenEditTeamDialog);
    
    return () => {
      window.removeEventListener('update-search-query', handleSearchUpdate as EventListener);
      window.removeEventListener('open-invite-dialog', handleOpenInviteDialog);
      window.removeEventListener('open-edit-team-dialog', handleOpenEditTeamDialog);
    };
  }, [permissions.canAddUsers, permissions.canEditTeamInfo]);

  // Effect to trigger content animation after loading
  useEffect(() => {
    if (!isLoading) {
      // Use timeout to allow initial rendering with max-height: 0
      const timer = setTimeout(() => {
        setIsContentLoaded(true);
      }, 50); // Small delay
      return () => clearTimeout(timer);
    } else {
      setIsContentLoaded(false); // Reset on re-load
    }
  }, [isLoading]);

  if (!selectedTeam && !isLoading) {
    return <EmptyView message="Please select a team to view team members." />;
  }

  if (error) {
    return <EmptyView message={`Error: ${error}`} />;
  }

  const handleEditTeam = async (newName: string) => {
    if (!selectedTeam) return;
    
    setIsEditingTeam(true);
    try {
      const { error } = await supabase
        .from('Teams')
        .update({ name: newName })
        .eq('id', selectedTeam.id);

      if (error) throw error;
      setSelectedTeam({ ...selectedTeam, name: newName });
      setIsEditTeamDialogOpen(false);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update team name');
    } finally {
      setIsEditingTeam(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    
    setIsEditingTeam(true);
    try {
      await deleteTeam(selectedTeam.id);
      setIsEditTeamDialogOpen(false);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete team');
    } finally {
      setIsEditingTeam(false);
    }
  };

  // Filter members and invites based on search query
  const filteredMembers = members
    .filter(member => {
      const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
      const email = member.email.toLowerCase();
      const query = searchQuery.toLowerCase();
      return fullName.includes(query) || email.includes(query);
    })
    .sort((a, b) => {
      // Owner comes first
      if (a.id === selectedTeam?.owner_profile_id) return -1;
      if (b.id === selectedTeam?.owner_profile_id) return 1;
      
      // Then admins
      const aIsAdmin = selectedTeam?.admins?.includes(a.id);
      const bIsAdmin = selectedTeam?.admins?.includes(b.id);
      if (aIsAdmin && !bIsAdmin) return -1;
      if (!aIsAdmin && bIsAdmin) return 1;
      
      // If roles are the same, sort by name
      return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
    });

  const filteredInvites = invites.filter(invite => {
    const email = invite.email.toLowerCase();
    const query = searchQuery.toLowerCase();
    return email.includes(query);
  });

  const handleInvite = async (email: string) => {
    setIsInviting(true);
    try {
      if (!selectedTeam) {
        throw new Error("Cannot invite member: No team selected.");
      }
      await inviteTeamMember(selectedTeam.id, email, members, invites);
      setIsInviteDialogOpen(false);
    } catch (error) {
      throw error;
    } finally {
      setIsInviting(false);
    }
  };

  const confirmDelete = (type: 'member' | 'invite', id: string) => {
    setDeleteTarget({ type, id });
    setShowConfirmDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget || !selectedTeam) return;

    try {
      if (deleteTarget.type === 'member') {
        const result = await deleteTeamMember(selectedTeam.id, deleteTarget.id, members);
        
        if (result.success) {
          // Update the local members list to reflect the change
          const updatedMembers = members.filter(member => member.id !== deleteTarget.id);
          setMembers(updatedMembers);
        } else {
          throw new Error(result.error);
        }
      } else {
        await deleteTeamInvite(deleteTarget.id);
      }
      setShowConfirmDialog(false);
      setDeleteTarget(null);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: 'admin' | 'reviewer') => {
    setIsUpdatingRole(true);
    try {
      if (!selectedTeam) {
        throw new Error('No team selected');
      }

      // Get current admins array
      const currentAdmins = selectedTeam.admins || [];

      // Update admins in the team based on the new role
      const newAdmins = newRole === 'admin'
        ? [...currentAdmins, memberId]
        : currentAdmins.filter(id => id !== memberId);

      // Update the team in the database
      const { data: updatedTeam, error: teamError } = await supabase
        .from('Teams')
        .update({ admins: newAdmins })
        .eq('id', selectedTeam.id)
        .select()
        .single();

      if (teamError) {
        throw teamError;
      }

      if (!updatedTeam) {
        throw new Error('Failed to update team');
      }

      // Update local state with the updated team data
      setSelectedTeam(updatedTeam);

      // Update members list to reflect the change
      setMembers(members.map(m => 
        m.id === memberId 
          ? { ...m, role: newRole }
          : m
      ));
    } catch (err: any) {
      throw err;
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const roleOptions = [{
    id: 'roles',
    items: [
      { id: 'admin', label: 'Admin', value: 'admin' },
      { id: 'reviewer', label: 'Reviewer', value: 'reviewer' }
    ]
  }];

  return (
    <>
      <div className="view-container team-container">
        {/* Show loader OR the animated content */}
        {isLoading ? (
          <div className="loading-container">
            <Button variant="text" size="large" isLoading={true} disabled={true} />
          </div>
        ) : (
          // Render the content div only when not loading
          <div className={`content ${isContentLoaded ? 'content-animate-enter-active' : 'content-animate-enter'}`}>
            {/* Content is now always present if this div renders */}
            <>
              <PaymentManager />

              {/* Members Section */}
              <div className="section">
                <div className="section-header">
                  <div className="header-title">Members</div>
                  <div className="header-email">Email</div>
                  <div className="header-role">Role</div>
                  <div className="header-actions"></div>
                </div>

                {filteredMembers.length === 0 ? (
                  <div className="empty-state">
                    <p>No team members found.</p>
                  </div>
                ) : (
                  <div className="list-container">
                    {filteredMembers.map(member => (
                      <div key={member.id} className="list-item">
                        <div className="member-item list-item-inset">
                          <div className="member-info">
                            <div className="member-name">
                              {member.first_name} {member.last_name}
                            </div>
                          </div>
                          <div className="member-email">{member.email}</div>
                          <div className="member-role" style={{ marginLeft: '-16px' }}>
                            {member.id === selectedTeam?.owner_profile_id ? (
                              <span className="owner-label">Owner</span>
                            ) : isCurrentUserOwner ? (
                              <Dropdown
                                items={roleOptions}
                                selectedId={selectedTeam?.admins?.includes(member.id) ? 'admin' : 'reviewer'}
                                onSelect={(value) => handleUpdateRole(member.id, value as 'admin' | 'reviewer')}
                                disabled={isUpdatingRole}
                                className="role-select"
                                label=""
                                hasSections={true}
                              />
                            ) : (
                              <span className="role-label">
                                {selectedTeam?.admins?.includes(member.id) ? 'admin' : 'reviewer'}
                              </span>
                            )}
                          </div>
                          <div className="delete-button-container">
                            {permissions.canRemoveUsers && member.id !== selectedTeam?.owner_profile_id && (
                              <Button 
                                variant="text"
                                size="small"
                                icon={deleteIcon}
                                iconAlt="Delete"
                                appearance="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDelete('member', member.id);
                                }}
                                className="delete-button"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Invites Section */}
              <div className="section">
                {filteredInvites.length > 0 && (
                  <div className="section-header">
                    <div className="header-title">Invites</div>
                    <div className="header-date">Date</div>
                    <div className="header-actions"></div>
                  </div>
                )}

                {filteredInvites.length === 0 && members.length < 3 ? (
                  <div className="list-container">
                    <EmptyStateEnhanced
                      icon={
                        <svg 
                          width="48" 
                          height="48" 
                          viewBox="0 0 24 24" 
                          fill="none"
                        >
                          <path fillRule="evenodd" clipRule="evenodd" d="M12 4C9.23858 4 7 6.23858 7 9C7 11.7614 9.23858 14 12 14C14.7614 14 17 11.7614 17 9C17 6.23858 14.7614 4 12 4ZM9 9C9 7.34315 10.3431 6 12 6C13.6569 6 15 7.34315 15 9C15 10.6569 13.6569 12 12 12C10.3431 12 9 10.6569 9 9Z" fill="currentColor"/>
                          <path d="M12.0004 15C8.83181 15 6.00184 16.4494 4.13739 18.7179C3.87439 19.0379 3.95723 19.5079 4.29569 19.7467L4.70424 20.035C5.04269 20.2738 5.50817 20.1903 5.77681 19.8751C7.2771 18.1143 9.50865 17 12.0004 17C14.4917 17 16.7228 18.1139 18.2231 19.8741C18.4918 20.1893 18.9573 20.2726 19.2957 20.0338L19.7042 19.7455C20.0426 19.5066 20.1254 19.0366 19.8623 18.7167C17.9979 16.4489 15.1684 15 12.0004 15Z" fill="currentColor"/>
                        </svg>
                      }
                      title="Invite team members"
                      subtitle="Collaborate with your team by inviting them to join this workspace"
                      buttonText={permissions.canAddUsers ? "Invite members" : undefined}
                      onButtonClick={permissions.canAddUsers ? () => setIsInviteDialogOpen(true) : undefined}
                    />
                  </div>
                ) : filteredInvites.length === 0 ? (
                  <div className="list-container">
                    <div className="empty-state">
                      <p>No pending invites.</p>
                    </div>
                  </div>
                ) : (
                  <div className="list-container">
                    {filteredInvites.map(invite => (
                      <div key={invite.id} className="list-item">
                        <div className="invite-item list-item-inset">
                          <div className="invite-info">
                            <div className="invite-email">{invite.email}</div>
                          </div>
                          <div className="invite-date">
                            {new Date(invite.created_at).toLocaleDateString()}
                          </div>
                          <div></div>
                          <div className="delete-button-container">
                            {permissions.canRemoveUsers && (
                              <Button 
                                variant="text"
                                size="small"
                                icon={deleteIcon}
                                iconAlt="Delete"
                                appearance="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDelete('invite', invite.id);
                                }}
                                className="delete-button"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          </div>
        )}
      </div>

      <Dialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title=""
        showCloseButton={false}
        actions={null}
        headerActions={null}
        className="confirm-delete-dialog"
      >
        <p>Are you sure you want to remove this {deleteTarget?.type === 'member' ? 'team member' : 'invite'}?</p>
        
        <div className="confirm-delete-actions">
          <Button 
            variant="secondary" 
            onClick={() => setShowConfirmDialog(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleDelete}
            size="small"
            className="delete"
          >
            Delete
          </Button>
        </div>
      </Dialog>

      <InviteDialog
        isOpen={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        onInvite={handleInvite}
        isLoading={isInviting}
      />

      <EditTeamDialog
        isOpen={isEditTeamDialogOpen}
        onClose={() => setIsEditTeamDialogOpen(false)}
        onSave={handleEditTeam}
        onDelete={permissions.canManageTeam ? handleDeleteTeam : undefined}
        isLoading={isEditingTeam}
        initialName={selectedTeam?.name || ''}
      />

      <div className="team-view-footer">
        {/* Consider adding pagination or load more functionality here */}
      </div>
    </>
  );
};

export default TeamView; 